// Canonical URL utility for managing canonical links across subdomain sites
// Generates canonical URLs that point from subdomain sites to milanlaser.com

// List of metro sites that should have canonical links to milanlaser.com
const CANONICAL_ENABLED_METROS = [
    'Atlanta, GA',
    'Boston, MA',
    'Chicago, IL',
    'Dallas, TX',
    'Detroit, MI',
    'Fort Myers, FL',
    'Houston, TX',
    'Kansas City, MO',
    'Las Vegas, NV',
    'Manhattan, NY',
    'Miami, FL',
    'Minneapolis, MN',
    'Phoenix, AZ',
    'Portland, OR',
    'Quincy, IL',
    'Salt Lake City, UT',
    'San Antonio, TX',
    'Tampa, FL',
    'Vancouver, WA',
    'Youngstown, OH',
];

// Default canonical URL mappings - consistent across all sites
const DEFAULT_CANONICAL_MAPPINGS = {
    '/specials/': 'https://milanlaser.com/specials',
    '/lp/google/': 'https://milanlaser.com/lp/google',
    '/areas/bikini/': 'https://milanlaser.com/areas/bikini',
    '/areas/brazilian/': 'https://milanlaser.com/areas/brazilian',
    '/areas/darkskin/': 'https://milanlaser.com/inclusive-clientele/dark-skin',
    '/areas/for-men/': 'https://milanlaser.com/inclusive-clientele/for-men',
    '/areas/': 'https://milanlaser.com/areas',
    '/areas/legs/': 'https://milanlaser.com/areas/legs',
    '/areas/lipnchin/': 'https://milanlaser.com/areas/face/lip-and-chin',
    '/areas/underarm/': 'https://milanlaser.com/areas/underarm',
    '/lp/': 'https://milanlaser.com/lp',
    '/locations/about-us/': 'https://milanlaser.com/company/about-us',
    '/locations/contact/': 'https://milanlaser.com/company/contact-us',
    '/locations/': 'https://milanlaser.com/locations',
    '/locations/reviews/': 'https://milanlaser.com/company/reviews',
    '/process/compare/': 'https://milanlaser.com/process/compare',
    '/process/faqs/': 'https://milanlaser.com/process/faqs',
    '/process/': 'https://milanlaser.com/process',
    '/process/quiz/': 'https://milanlaser.com/process/quiz',
    '/specials/estimate/': 'https://milanlaser.com/specials/estimate',
    '/form/processing/': 'https://milanlaser.com/form/processing',
    '/form/self-success-force/': 'https://milanlaser.com/form/self-success-force',
    '/process/beforeafter/beforeafter_bikini/': 'https://milanlaser.com/process/beforeafter/beforeafter_bikini',
    '/process/beforeafter/beforeafter_legs/': 'https://milanlaser.com/process/beforeafter/beforeafter_legs',
    '/process/beforeafter/beforeafter_lip/': 'https://milanlaser.com/process/beforeafter/beforeafter_lip',
    '/process/beforeafter/beforeafter_men/': 'https://milanlaser.com/process/beforeafter/beforeafter_men',
    '/process/beforeafter/beforeafter_underarm/': 'https://milanlaser.com/process/beforeafter/beforeafter_underarm',
    '/process/beforeafter/': 'https://milanlaser.com/process/beforeafter',
};

// Generates the canonical URL for a given path and metro data
// Returns null if no canonical URL should be generated (metro not enabled)
export function getCanonicalUrl(pathname, currentMetro) {
    // Check if this metro is enabled for canonical links
    if (!isCanonicalEnabled(currentMetro.name, currentMetro.stateAbbrev)) {
        return null;
    }

    // Handle homepage - points to the metro page on milanlaser.com
    if (pathname === '/') {
        const firstStore = currentMetro.stores && currentMetro.stores[0];
        if (firstStore && firstStore.urlPath) {
            // Extract metro-level URL from store urlPath
            const urlParts = firstStore.urlPath.split('/');
            if (urlParts.length >= 4) {
                const metroPath = urlParts.slice(0, 4).join('/');
                return `https://milanlaser.com${metroPath}`;
            }
        }

        // Fallback to constructed URL if urlPath manipulation fails
        const state = currentMetro.stateAbbrev.toLowerCase();
        const metro = currentMetro.metroPath;
        return `https://milanlaser.com/locations/${state}/${metro}`;
    }

    // Handle location-specific pages - points to specific store location page
    if (pathname.startsWith('/locations/') && currentMetro.stores && currentMetro.stores.length > 0) {
        const locationPath = pathname.replace('/locations/', '').replace(/\/$/, '');

        // If it's the metro path, find the first store and use its full urlPath
        if (locationPath === currentMetro.metroPath) {
            const firstStore = currentMetro.stores[0];
            if (firstStore && firstStore.urlPath) {
                return `https://milanlaser.com${firstStore.urlPath}`;
            }
        }

        // Check for specific store paths - use full urlPath
        const matchingStore = currentMetro.stores.find(
            (store) => locationPath === store.pathname || locationPath.includes(store.pathname)
        );

        if (matchingStore && matchingStore.urlPath) {
            return `https://milanlaser.com${matchingStore.urlPath}`;
        }
    }

    // Check if there's a default mapping for this path
    if (DEFAULT_CANONICAL_MAPPINGS[pathname]) {
        return DEFAULT_CANONICAL_MAPPINGS[pathname];
    }

    // If no mapping exists and metro is enabled, return null (no canonical link)
    return null;
}

// Check if a metro is enabled for canonical linking
export function isCanonicalEnabled(metroName, stateAbbrev) {
    return CANONICAL_ENABLED_METROS.includes(`${metroName}, ${stateAbbrev}`);
}

// Add a metro to the canonical enabled list
export function addCanonicalEnabledMetro(metroName) {
    if (Array.isArray(metroName)) {
        metroName.forEach((name) => {
            if (!CANONICAL_ENABLED_METROS.includes(name)) {
                CANONICAL_ENABLED_METROS.push(name);
            }
        });
    } else if (!CANONICAL_ENABLED_METROS.includes(metroName)) {
        CANONICAL_ENABLED_METROS.push(metroName);
    }
}
